import React from 'react';
import {
  CartLoadingStates,
  CartState,
  CartAction,
  PLACE_ORDER_TRANSACTION_DECLINED,
  PLACE_ORDER_FAILURE
} from './constants';
import { LoadingState, SUCCESS, FAILURE, FETCHING, NOT_FOUND } from 'store/constants';
import { assign } from 'helpers/objects';
import { CustomerLoadableEntity } from 'store/customer/helpers';
import { CustomerLoadingStates } from '../customer/constants';
import { toast } from 'react-toastify';
import ErrorModal from '../../components/ErrorModal';
import {
  getAppliedStoreCreditFromTotalSegments,
  getGiftCardsFromTotalSegments,
  getInternalPaymentMethodsFromTotalSegments,
  InternalPaymentMethod
} from './magentoSelectors';
import { CartPaymentMethod, findCustomerPaymentMethodInCartPaymentMethods } from 'helpers/billing';
import { optionGet } from 'faunctions';
import { GiftCardProductType } from '../../types/Product';
import { parseIfJson } from 'helpers/strings';
import { ERR_OOS, isMagentoErrorCode, isMagentoJsonError } from 'store/magento/constants';
import { newrelic } from 'helpers/reporting/newrelic';
import {
  AddItemToCartRequestData,
  analyticsTrackCheckoutError,
  fetchCartRequest,
  itemOutOfStockFailure,
  placeOrderFailure
} from 'store/cart/actions';
import { createAction } from 'redux-actions';
import { isOutOfStockMessage } from 'store/cart/sagas';
import type { Dispatch, Action } from 'redux';
import { ApiRequestFailure, setUrlParams } from 'helpers/http';
import { Cart__Patched } from 'pages/checkout/cart';
import QuoteDataTotalSegment from 'mage-swagfaces/quote/QuoteDataTotalSegment';
import { shouldGoToEditorV2 } from 'helpers/projects/redirect';
import { getCurrentURL, redirectToPage } from 'helpers/navigation';
import { openCustomerLoginModal } from 'store/modal/actions';
import Itly from 'itly';

export type CartLoadableEntity =
  | 'cart'
  | 'cartItems'
  | 'shippingAddress'
  | 'paymentMethods'
  | 'shippingRates'
  | 'promoCode'
  | 'giftCard'
  | 'placeOrder'
  | 'storeCredit';

export type EditorUrlQueryParams = {
  sku: string;
  qty: string;
  attributes: Record<string, string>;
};

/**
 * Takes an array of loadable cart entities to be updated with the specified loadingState
 *
 * ex:
 *
 * loadingState = {
 *  cartItems: INITIALIZED,
 *  shippingAddress: INITIALIZED,
 *  shippingRates: INITIALIZED,
 * }
 *
 * updateCartLoadingStates(["cartItems", "shippingAddress"], FETCHING)(cartState)
 */
export const updateLoadingStates =
  (entities: CartLoadableEntity[] | CustomerLoadableEntity[], loadingState: LoadingState) =>
  (loadingStates: CartLoadingStates | CustomerLoadingStates): CartLoadingStates | CustomerLoadingStates => {
    let updatedStates: CartLoadingStates;

    entities.forEach((entity) => {
      updatedStates = {
        ...updatedStates,
        [entity]: loadingState
      };
    });

    return assign<CartLoadingStates | CustomerLoadingStates>(loadingStates)(updatedStates);
  };

export const updateCartSuccess =
  (loadableEntities: CartLoadableEntity[]) =>
  (cartLoadingStates: CartLoadingStates): CartLoadingStates =>
    updateLoadingStates(loadableEntities, SUCCESS)(cartLoadingStates) as CartLoadingStates;

export const updateCartFailure =
  (loadableEntities: CartLoadableEntity[]) =>
  (cartLoadingStates: CartLoadingStates): CartLoadingStates =>
    updateLoadingStates(loadableEntities, FAILURE)(cartLoadingStates) as CartLoadingStates;

export const updateCartNotFound =
  (loadableEntities: CartLoadableEntity[]) =>
  (cartLoadingStates: CartLoadingStates): CartLoadingStates =>
    updateLoadingStates(loadableEntities, NOT_FOUND)(cartLoadingStates) as CartLoadingStates;

export const updateCartFetching =
  (loadableEntities: CartLoadableEntity[]) =>
  (cartLoadingStates: CartLoadingStates): CartLoadingStates =>
    updateLoadingStates(loadableEntities, FETCHING)(cartLoadingStates) as CartLoadingStates;

// React toastify helper to render custom Toast component
export const renderErrorToast = (message: string, raw?: boolean): string | number =>
  toast.error(<ErrorModal message={message} raw={raw} />);

/**
 * This aggregates any UI-applied CustomerPaymentMethods (paypal, email) from the previous state, with any internal
 * payment methods (gift card, store credit) we may get in an updated cart response.
 *
 * This would most likely probably go away with completion of https://artifactteam.atlassian.net/browse/EN-355
 */
export const combineCartPaymentMethods = (
  state: CartState,
  action: CartAction
): (CartPaymentMethod | InternalPaymentMethod)[] => {
  const maybeCartPaymentMethods: CartPaymentMethod[] = optionGet('entity.paymentMethods')(state).getOrElse([]);
  const maybeCustomerPaymentMethod = findCustomerPaymentMethodInCartPaymentMethods(maybeCartPaymentMethods);
  const totalSegments = optionGet('payload.cart.total_segments')(action).getOrElse([]);
  const internalPaymentMethods = getInternalPaymentMethodsFromTotalSegments(totalSegments);

  if (!maybeCustomerPaymentMethod.type) {
    // Omit any empty payment methods (where type === null)
    return [...internalPaymentMethods];
  }

  return [maybeCustomerPaymentMethod, ...internalPaymentMethods];
};

export const cartContainsGiftCard = (cart: Cart__Patched): boolean =>
  cart.items.some((item) => item.product_type === GiftCardProductType);

export const cartPaymentIsGiftcardOrSiteCredit = (totalSegments: QuoteDataTotalSegment[]): number | boolean => {
  const storeCredit = getAppliedStoreCreditFromTotalSegments(totalSegments);
  const giftCards = getGiftCardsFromTotalSegments(totalSegments);

  if (giftCards.length || Math.abs(storeCredit) > 0) {
    return true;
  }

  return false;
};

export const createPlaceOrderError = (
  dispatch: Dispatch,
  err: ApiRequestFailure,
  paymentMethod: CartPaymentMethod,
  cartId: number
): void => {
  const errMsg = optionGet('body.message')(err).getOrElse('Something went wrong.');
  dispatch(analyticsTrackCheckoutError(err, err.body.message, cartId));

  try {
    const jsonMsg = parseIfJson(errMsg);
    let isOutOfStock = false;
    if (jsonMsg && isOutOfStockMessage(jsonMsg)) {
      isOutOfStock = jsonMsg?.body?.errorCode === ERR_OOS;
    }
    if (typeof errMsg === 'string' && errMsg.toLowerCase().includes('transaction has been declined')) {
      newrelic('addPageAction')(PLACE_ORDER_TRANSACTION_DECLINED, { err });
    } else if (isOutOfStock && isOutOfStockMessage(jsonMsg)) {
      dispatch(itemOutOfStockFailure(errMsg, { items: jsonMsg.body.data.items }));
    } else {
      newrelic('addPageAction')(PLACE_ORDER_FAILURE, { err });
    }
    if (isMagentoJsonError(err.body) && isMagentoErrorCode(err.body.errorCode)) {
      dispatch(createAction(err.body.errorCode)({ data: err.body.data }) as Action<any>);
    } else if (typeof errMsg === 'string' && errMsg.toLowerCase().includes('no longer valid and has been removed.')) {
      //expired promo code
      dispatch(placeOrderFailure(paymentMethod, err.body.message));
      dispatch(fetchCartRequest());
    } else if (!isOutOfStock) {
      dispatch(placeOrderFailure(paymentMethod, err.body.message));
    }
  } catch (error) {
    console.error('Error parsing place order error message', error);
  }
};

export const redirectToEditor = async (
  dispatch: Dispatch,
  item: AddItemToCartRequestData,
  isLoggedIn: boolean,
  customerEmail: string,
  customerId: string,
  skipLogin = false
): Promise<void> => {
  const redirectParams = {
    sku: item.sku,
    qty: String(item.qty),
    attributes: item.options
  };

  let redirectUrl = buildEditorRedirectUrl(redirectParams, item.editorVersion);

  if (!isLoggedIn && !skipLogin) {
    Itly.loginStarted({ login_type: 'Project' });
    let redirectTo = `login?redirectTo=${encodeURIComponent(redirectUrl)}`;

    if (item.editorVersion === '2') {
      redirectTo = setUrlParams(redirectTo, {
        tryEditorV2ForSku: item.sku,
        tryEditorV2ForHammerCategory: item.hammerCategory
      });
    }
    dispatch(openCustomerLoginModal({ redirect: `${getCurrentURL()}/${redirectTo}` }));
    return;
  }

  // (copied from original saga)
  // If the hammer category is a Magento traffic type Split, we can only check if the customer is logged in
  // Since we have two different traffic types, we need to ask for the Split treatment differently... a bit jank but
  // This problem will persist until we have consistent traffic types among tests
  if (item.hammerCategory || item.sku) {
    const continueToV2 = await shouldGoToEditorV2(
      item.sku,
      item.hammerCategory,
      customerEmail || '',
      customerId || '',
      item.editorVersion
    );

    if (!continueToV2) {
      redirectUrl = redirectUrl.replace(process.env.GATSBY_EDITOR_V2_URL!, process.env.GATSBY_EDITOR_URL!);
    }
  }

  await redirectToPage(redirectUrl, { replace: true });
};

export const buildEditorRedirectUrl = (params: EditorUrlQueryParams, editorVersion: string): string => {
  const editorUrl = editorVersion === '2' ? process.env.GATSBY_EDITOR_V2_URL : process.env.GATSBY_EDITOR_URL;

  const flatParams: Record<string, string> = {
    sku: params.sku,
    qty: String(params.qty),
    ...flattenAttributes(params.attributes)
  };

  const searchParams = new URLSearchParams(flatParams);

  return `${editorUrl}?${searchParams.toString()}&newProject=true`;
};

const flattenAttributes = (attributes: Record<string, string>): Record<string, string> => {
  const result: Record<string, string> = {};
  for (const [key, value] of Object.entries(attributes)) {
    result[`attributes[${key}]`] = value;
  }
  return result;
};
